import { useLazyQuery, useQuery, gql } from "@apollo/client"
import { useState } from "react"
import { TAG_FIELDS } from "../tags/useQueryTags"

const KNOWLEDGES_LIMIT = 20

const KNOWLEDGE_TYPE_ARTICLE = "KnowledgeArticle"
const KNOWLEDGE_TYPE_FILE = "KnowledgeFile"
const KNOWLEDGE_TYPE_TRAINING_MODULE = "KnowledgeTrainingModule"

const UPLOAD_FIELDS = gql`
  fragment UploadFields on UploadInfo {
    id
    key
    fileName
    fileGroup
    fileSize
    contentType
    customSize {
      width
    }
  }
`

const KNOWLEDGE_FIELDS = gql`
  fragment KnowledgeFields on KnowledgeV2 {
    __typename
    ... on ${KNOWLEDGE_TYPE_ARTICLE} {
      id
      title
      isRestrictedToKiosk
      category {
        id
        name
        breadcrumbs
        ancestors
      }
      tags {
        ...TagFields
      }
      groups {
        id
      }
      locations {
        id
      }
      content {
        ... on KnowledgeContentText {
          id
          heading
          text
          textUploads {
            ...UploadFields
          }
        }
        ... on KnowledgeContentImage {
          id
          heading
          imageUploads {
            ...UploadFields
          } 
        }
        ... on KnowledgeContentFiles {
          id
          heading
          fileUploads {
            ...UploadFields
          }
        }
        ... on KnowledgeContentNote {
          id
          heading
          type
          text
        }
        ... on KnowledgeContentVideo {
          id
          heading
          url
        }
        ... on KnowledgeContentMultichoice {
          id
          heading
          question
          answers {
            id
            text
            isCorrect
          }
        }
      }
      author {
        id
        firstName
        lastName
        avatar {
          key
        }
      }
      createdAt
      updatedAt
      deleted
    }
    ... on ${KNOWLEDGE_TYPE_FILE} {
      id
      title
      isRestrictedToKiosk
      category {
        id
        name
      }
      tags {
        ...TagFields
      }
      groups {
        id
      }
      locations {
        id
      }
      author {
        id
        firstName
        lastName
        avatar {
          key
        }
      }
      file {
        id
        key
        fileGroup
        fileSize
        fileName
      }
      createdAt
      updatedAt
    }
    ... on ${KNOWLEDGE_TYPE_TRAINING_MODULE} {
      id
      content {
        ... on KnowledgeContentText {
          id
          heading
          text
        }
        ... on KnowledgeContentImage {
          id
          heading
          imageUploads {
            ...UploadFields
          }
        }
        ... on KnowledgeContentFiles {
          id
          heading
          fileUploads {
            ...UploadFields
          }
        }
        ... on KnowledgeContentNote {
          id
          heading
          type
          text
        }
        ... on KnowledgeContentVideo {
          id
          heading
          url
        }
        ... on KnowledgeContentMultichoice {
          id
          heading
          question
          answers {
            id
            text
            isCorrect
          }
        }
      }
    }
  }
  ${TAG_FIELDS}
  ${UPLOAD_FIELDS}
`

const KNOWLEDGES_FIELDS = gql`
  fragment KnowledgesFields on KnowledgeV2 {
    __typename
    ... on ${KNOWLEDGE_TYPE_ARTICLE} {
      id
      title
      isRestrictedToKiosk
      category {
        id
        name
      }
      tags {
        ...TagFields
      }
      groups {
        id
      }
      locations {
        id
      }
      author {
        id
        firstName
        lastName
        avatar {
          key
        }
      }
      createdAt
      updatedAt
      deleted
    }
    ... on ${KNOWLEDGE_TYPE_FILE} {
      id
      title
      isRestrictedToKiosk
      category {
        id
        name
      }
      tags {
        ...TagFields
      }
      groups {
        id
      }
      locations {
        id
      }
      author {
        id
        firstName
        lastName
        avatar {
          key
        }
      }
      file {
        id
        key
        fileGroup
        fileSize
        fileName
      }
      createdAt
      updatedAt
    }
  }
  ${TAG_FIELDS}
`

const KNOWLEDGES_QUERY = gql`
  query Knowledges($filter: KnowledgeFilterInput, $orderBy: String, $limit: Int, $offset: Int, $isArticle: Boolean) {
    knowledges(filter: $filter, orderBy: $orderBy, limit: $limit, offset: $offset, isArticle: $isArticle) {
      ...KnowledgesFields
    }
  }
  ${KNOWLEDGES_FIELDS}
`

const RECENT_KNOWLEDGES_QUERY = gql`
  query Knowledges($filter: KnowledgeFilterInput, $orderBy: String, $limit: Int, $offset: Int) {
    knowledges(filter: $filter, orderBy: $orderBy, limit: $limit, offset: $offset, isArticle: true) {
      ...KnowledgeFields
    }
  }
  ${KNOWLEDGE_FIELDS}
`

const KNOWLEDGE_QUERY = gql`
  query Knowledge($id: ID!) {
    knowledge: knowledgeV2(id: $id) {
      ...KnowledgeFields
    }
  }
  ${KNOWLEDGE_FIELDS}
`

const KNOWLEDGE_CREATED_SUBSCRIPTION = gql`
  subscription KnowledgeCreated($category: ID!) {
    knowledgeCreated(category: $category) {
      ...KnowledgeFields
    }
  }
  ${KNOWLEDGE_FIELDS}
`

const KNOWLEDGE_UPDATED_SUBSCRIPTION = gql`
  subscription KnowledgeUpdated {
    knowledgeUpdated {
      ...KnowledgeFields
    }
  }
  ${KNOWLEDGE_FIELDS}
`

const KNOWLEDGE_MOVED_SUBSCRIPTION = gql`
  subscription KnowledgeMoved {
    knowledgeMoved {
      ...KnowledgeFields
    }
  }
  ${KNOWLEDGE_FIELDS}
`

const KNOWLEDGE_DELETED_SUBSCRIPTION = gql`
  subscription KnowledgeDeleted {
    knowledgeDeleted {
      __typename
      ... on ${KNOWLEDGE_TYPE_ARTICLE} {
        id
      }
      ... on ${KNOWLEDGE_TYPE_FILE} {
        id
      }
      ... on ${KNOWLEDGE_TYPE_TRAINING_MODULE} {
        id
      }
    }
  }
`

const subscribe = (result, categoryId) => () => {
  if (!categoryId) return []

  return [
    result.subscribeToMore({
      document: KNOWLEDGE_CREATED_SUBSCRIPTION,
      variables: {
        category: categoryId
      },
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data) return prev

        const newItem = subscriptionData.data.knowledgeCreated

        // Skip if already exists
        if (prev.knowledges.some((p) => p.id === newItem.id)) return prev

        return { ...prev, knowledges: [newItem, ...prev.knowledges] }
      },
      onError: (err) => console.log("[useQueryKnowledges][KNOWLEDGE_CREATED_SUBSCRIPTION(1)]", err)
    }),
    result.subscribeToMore({
      document: KNOWLEDGE_UPDATED_SUBSCRIPTION,
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data) return prev
        const updatedItem = subscriptionData.data.knowledgeUpdated

        // Update the item in place
        return {
          ...prev,
          knowledges: prev.knowledges.map((p) => (p.id === updatedItem.id ? updatedItem : p))
        }
      },
      onError: (err) => console.log("[useQueryKnowledges][KNOWLEDGE_UPDATED_SUBSCRIPTION]", err)
    }),
    result.subscribeToMore({
      document: KNOWLEDGE_MOVED_SUBSCRIPTION,
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data) return prev
        const movedId = subscriptionData.data.knowledgeMoved.id
        const list = prev.knowledges.filter((k) => k.id !== movedId)
        const uniq = Array.from(new Map(list.map((i) => [i.id, i])).values())

        return { ...prev, knowledges: uniq }
      },
      onError: (err) => console.log("[KNOWLEDGE_MOVED_SUBSCRIPTION]", err)
    }),
    result.subscribeToMore({
      document: KNOWLEDGE_DELETED_SUBSCRIPTION,
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData?.data) return prev
        const deletedId = subscriptionData.data.knowledgeDeleted?.id
        if (!deletedId) return prev

        const list = (prev.knowledges || []).filter((k) => k.id !== deletedId)
        const uniq = Array.from(new Map(list.map((i) => [i.id, i])).values())

        return { ...prev, knowledges: uniq }
      },
      onError: (err) => console.error("[KNOWLEDGE_DELETED_SUBSCRIPTION]", err)
    })
  ]
}

const subscribeSingle = (result) => () => [
  result.subscribeToMore({
    document: KNOWLEDGE_UPDATED_SUBSCRIPTION,
    onError: (err) => console.log("[useQueryKnowledges][KNOWLEDGE_UPDATED_SUBSCRIPTION(2)]", err)
  })
]

// Helper function for load more logic
const createLoadMoreHandler = (result, setHasMore) => async () => {
  if (result.loading || !result.data || !result.data.knowledges) return

  try {
    // Use the full count of knowledges for the offset calculation
    const currentCount = result.data.knowledges.filter(
      (knowledge) => knowledge.__typename === KNOWLEDGE_TYPE_ARTICLE
    ).length

    const { data } = await result.fetchMore({
      variables: {
        ...result.variables,
        offset: currentCount,
        limit: KNOWLEDGES_LIMIT,
        isArticle: true
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev
        const incoming = fetchMoreResult.knowledges
        const merged = [
          ...prev.knowledges,
          ...incoming.filter((item) => !prev.knowledges.some((p) => p.id === item.id))
        ]
        return { ...prev, knowledges: merged }
      }
    })

    const fetchedKnowledges =
      data?.knowledges.filter((knowledge) => knowledge.__typename === KNOWLEDGE_TYPE_ARTICLE) || []
    const newHasMore = fetchedKnowledges.length === KNOWLEDGES_LIMIT
    setHasMore(newHasMore)

    return data
  } catch (error) {
    console.error("[useQueryKnowledges][loadMore]", error)
    setHasMore(false)
    return null
  }
}

const useQueryKnowledges = (options) => {
  const { variables, ...otherOptions } = options || {}

  const result = useQuery(KNOWLEDGES_QUERY, {
    variables: {
      offset: 0,
      limit: KNOWLEDGES_LIMIT,
      isArticle: false,
      ...variables
    },
    fetchPolicy: "cache-and-network",
    ...otherOptions
  })

  // Separate articles and files
  const articles = result.data?.knowledges.filter((knowledge) => knowledge.__typename === KNOWLEDGE_TYPE_ARTICLE) || []
  const files = result.data?.knowledges.filter((knowledge) => knowledge.__typename === KNOWLEDGE_TYPE_FILE) || []

  return {
    ...result,
    subscribe: subscribe(result),
    articles,
    files
  }
}

const useQueryRecentKnowledges = (options) => {
  const { variables, ...otherOptions } = options || {}

  const result = useQuery(RECENT_KNOWLEDGES_QUERY, {
    variables: {
      offset: 0,
      limit: KNOWLEDGES_LIMIT,
      ...variables
    },
    fetchPolicy: "cache-first",
    ...otherOptions
  })

  const articles = result.data?.knowledges.filter((knowledge) => knowledge.__typename === KNOWLEDGE_TYPE_ARTICLE) || []

  return {
    ...result,
    subscribe: subscribe(result),
    articles
  }
}

const useQueryKnowledge = (options) => {
  const result = useQuery(KNOWLEDGE_QUERY, {
    fetchPolicy: "cache-and-network",
    ...options
  })

  return {
    ...result,
    subscribe: subscribeSingle(result)
  }
}

const useLazyQueryKnowledges = (options) => {
  const [hasMore, setHasMore] = useState(false)
  const { variables, ...otherOptions } = options || {}

  // Use consistent isArticle value across the initial query and loadMore call
  const [loadKnowledges, result] = useLazyQuery(KNOWLEDGES_QUERY, {
    variables: {
      offset: 0,
      limit: KNOWLEDGES_LIMIT,
      isArticle: false,
      ...variables
    },
    fetchPolicy: "cache-and-network",
    ...otherOptions,
    onCompleted: (data) => {
      if (data && data.knowledges) {
        const articlesCount = data.knowledges.filter(
          (knowledge) => knowledge.__typename === KNOWLEDGE_TYPE_ARTICLE
        ).length
        setHasMore(articlesCount === KNOWLEDGES_LIMIT)
      }
    }
  })

  // Since isArticle is always true in this hook, the knowledges data should contain only articles.
  const articles = result.data?.knowledges.filter((knowledge) => knowledge.__typename === KNOWLEDGE_TYPE_ARTICLE) || []
  const files = result.data?.knowledges.filter((knowledge) => knowledge.__typename === KNOWLEDGE_TYPE_FILE) || []

  return [
    loadKnowledges,
    {
      ...result,
      loadMore: createLoadMoreHandler(result, setHasMore),
      subscribe: subscribe(result, variables?.filter?.category),
      articles,
      files,
      hasMore
    }
  ]
}

const useLazyQueryKnowledge = () => useLazyQuery(KNOWLEDGE_QUERY)

// Type policies for knowledges
const knowledgesTypePolicies = {
  scoped: {
    Query: {
      fields: {
        knowledges: {
          keyArgs: ["filter", "isArticle", "orderBy"],
          merge(existing = [], incoming, { args }) {
            if (args?.offset) {
              const merged = [...existing, ...incoming]
              return Array.from(new Map(merged.map((i) => [i.id, i])).values())
            }
            return incoming
          }
        }
      }
    }
  }
}

export {
  useQueryKnowledges,
  useQueryKnowledge,
  useLazyQueryKnowledge,
  useLazyQueryKnowledges,
  useQueryRecentKnowledges,
  KNOWLEDGE_FIELDS,
  KNOWLEDGES_QUERY,
  knowledgesTypePolicies
}
